import React, { useEffect, useState } from 'react';

const FormModal = ({
    isEditing,
    currentForm,
    handleFormChange,
    handleSubmit,
    handleFieldChange,
    fieldState,
    fieldTypes,
    handleAddField,
    handleRemoveField,
    setShowModal,
    error
}) => {
    const [formErrors, setFormErrors] = useState({});
    const [parsedUrl, setParsedUrl] = useState({ domain: '', path: '' });

    // Parse domain from sourceUrl when it changes
    useEffect(() => {
        if (currentForm.sourceUrl) {
            try {
                const url = new URL(currentForm.sourceUrl);
                setParsedUrl({
                    domain: url.hostname,
                    path: url.pathname + url.search
                });
            } catch (e) {
                setParsedUrl({ domain: 'Invalid URL', path: '' });
            }
        } else {
            setParsedUrl({ domain: '', path: '' });
        }
    }, [currentForm.sourceUrl]);
    
    // Validate form before submission
    const validateForm = (e) => {
        e.preventDefault();
        const errors = {};

        // Required fields validation
        if (!currentForm.sourceUrl) errors.sourceUrl = 'Source URL is required';
        if (!currentForm.alias) errors.alias = 'Alias is required';

        // URL validation
        if (currentForm.sourceUrl) {
            try {
                new URL(currentForm.sourceUrl);
            } catch (e) {
                errors.sourceUrl = 'Invalid URL format';
            }
        }

        setFormErrors(errors);

        // If no errors, proceed with form submission
        if (Object.keys(errors).length === 0) {
            handleSubmit(e);
        }
    };

    // Format field types for better display
    const formatFieldType = (type) => {
        const typeMap = {
            'text': 'Text',
            'email': 'Email',
            'tel': 'Telephone',
            'number': 'Number',
            'date': 'Date',
            'textarea': 'Text Area',
            'select': 'Dropdown',
            'radio': 'Radio Buttons',
            'checkbox': 'Checkbox'
        };
        return typeMap[type] || type;
    };

    // Format field value based on type
    const getFormattedValue = (value, type) => {
        if (value === null || value === undefined) return '';
        
        switch (type) {
            case 'checkbox':
                // For checkboxes, convert to string to display 'true' or 'false'
                return value.toString();
            case 'number':
                // For numbers, make sure it's displayed as a number
                return Number(value);
            default:
                // For text and other types, return as string
                return value.toString();
        }
    };

    // Render appropriate input based on field type
    const renderFieldInput = () => {
        switch (fieldState.fieldType) {
            case 'checkbox':
                return (
                    <label className="inline-flex items-center cursor-pointer text-sm text-gray-700">
                        <input
                            type="checkbox"
                            name="defaultValue"
                            checked={fieldState.defaultValue === "true" || fieldState.defaultValue === true}
                            onChange={(e) => {
                                handleFieldChange({
                                    target: {
                                        name: 'defaultValue',
                                        value: e.target.checked
                                    }
                                });
                            }}
                            className="rounded border-gray-300 text-blue-600 shadow-sm focus:ring-blue-500 h-4 w-4 mr-2"
                        />
                        Default Checked
                    </label>
                );
            case 'number':
                return (
                    <input
                        id="defaultValue"
                        type="number"
                        name="defaultValue"
                        value={fieldState.defaultValue}
                        onChange={handleFieldChange}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Optional default value"
                    />
                );
            case 'date':
                return (
                    <input
                        id="defaultValue"
                        type="date"
                        name="defaultValue"
                        value={fieldState.defaultValue}
                        onChange={handleFieldChange}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                );
            case 'select':
            case 'radio':
                return (
                    <input
                        id="defaultValue"
                        type="text"
                        name="defaultValue"
                        value={fieldState.defaultValue}
                        onChange={handleFieldChange}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Value of default selected option"
                    />
                );
            default:
                return (
                    <input
                        id="defaultValue"
                        type="text"
                        name="defaultValue"
                        value={fieldState.defaultValue}
                        onChange={handleFieldChange}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Optional default value"
                    />
                );
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-500 bg-opacity-75 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">
                        {isEditing ? 'Edit Form' : 'Add New Form'}
                    </h3>
                    <button
                        type="button"
                        className="text-gray-400 hover:text-gray-500 focus:outline-none"
                        onClick={() => setShowModal(false)}
                    >
                        <span className="sr-only">Close</span>
                        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={validateForm} className="px-6 py-4">
                    <h4 className="text-base font-medium text-gray-900 mb-4">Basic Information</h4>
                    <div className="space-y-4">
                        {/* Alias field */}
                        <div>
                            <label htmlFor="alias" className="block text-sm font-medium text-gray-700">
                                Alias <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="alias"
                                type="text"
                                name="alias"
                                value={currentForm.alias || ''}
                                onChange={handleFormChange}
                                className={`mt-1 block w-full border ${formErrors.alias ? 'border-red-300 bg-red-50' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                placeholder="e.g., Contact Form"
                            />
                            {formErrors.alias && (
                                <p className="mt-1 text-sm text-red-600">{formErrors.alias}</p>
                            )}
                            <p className="mt-1 text-xs text-gray-500">A friendly name to easily identify this form</p>
                        </div>

                        {/* Source URL field */}
                        <div>
                            <label htmlFor="sourceUrl" className="block text-sm font-medium text-gray-700">
                                Source URL <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="sourceUrl"
                                type="text"
                                name="sourceUrl"
                                value={currentForm.sourceUrl || ''}
                                onChange={handleFormChange}
                                className={`mt-1 block w-full border ${formErrors.sourceUrl ? 'border-red-300 bg-red-50' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                placeholder="e.g., https://example.com/contact"
                            />
                            {formErrors.sourceUrl && (
                                <p className="mt-1 text-sm text-red-600">{formErrors.sourceUrl}</p>
                            )}
                            <p className="mt-1 text-xs text-gray-500">The full URL where this form appears</p>
                        </div>

                        {/* Auto-detected domain info */}
                        {parsedUrl.domain && (
                            <div className="bg-gray-50 rounded-md p-3 border border-gray-200">
                                <span className="text-sm font-medium text-gray-500">Domain (auto-detected):</span>
                                <span className="ml-2 text-sm text-gray-900">{parsedUrl.domain}</span>
                            </div>
                        )}

                        {/* Form ID field */}
                        <div>
                            <label htmlFor="formId" className="block text-sm font-medium text-gray-700">
                                Form ID
                            </label>
                            <input
                                id="formId"
                                type="text"
                                name="formId"
                                value={currentForm.formId || ''}
                                onChange={handleFormChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                placeholder="e.g., contact-form-1"
                            />
                            <p className="mt-1 text-xs text-gray-500">The HTML id attribute of the form (the id='value' attribute in the form tag)</p>
                        </div>

                        {/* Form Name field */}
                        <div>
                            <label htmlFor="formName" className="block text-sm font-medium text-gray-700">
                                Form Name
                            </label>
                            <input
                                id="formName"
                                type="text"
                                name="formName"
                                value={currentForm.formName || ''}
                                onChange={handleFormChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                placeholder="e.g., contact_form"
                            />
                            <p className="mt-1 text-xs text-gray-500">The HTML name attribute of the form (the name='value' attribute in the form tag)</p>
                        </div>

                        {/* Description field */}
                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                Description
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                value={currentForm.description || ''}
                                onChange={handleFormChange}
                                rows="3"
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                placeholder="Add notes about this form's purpose or usage"
                            ></textarea>
                        </div>
                    </div>

                    {/* Form Fields Section */}
                    <div className="mt-8 border-t border-gray-200 pt-6">
                        <h4 className="text-base font-medium text-gray-900 flex items-center">
                            Form Fields
                            <span className="ml-2 text-sm bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                                {currentForm.fields.length}
                            </span>
                        </h4>

                        {/* Existing Fields List */}
                        {currentForm.fields.length > 0 ? (
                            <div className="mt-4 border border-gray-200 rounded-md overflow-hidden max-h-56 overflow-y-auto bg-gray-50">
                                <ul className="divide-y divide-gray-200">
                                    {currentForm.fields.map((field, index) => (
                                        <li key={`field-${index}`} className="p-4 hover:bg-gray-100 bg-white">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-2 flex-wrap">
                                                    <span className="font-medium text-gray-900">{field.fieldName}</span>
                                                    <span className="px-2 py-0.5 rounded-full text-xs bg-gray-200 text-gray-800">
                                                        {formatFieldType(field.fieldType)}
                                                    </span>
                                                    {field.required && (
                                                        <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-800">
                                                            Required
                                                        </span>
                                                    )}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveField(index)}
                                                    className="inline-flex items-center justify-center rounded-full h-6 w-6 bg-red-100 text-red-600 hover:bg-red-200"
                                                >
                                                    <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                    </svg>
                                                </button>
                                            </div>

                                            {(field.defaultValue !== undefined || field.validation.minLength || field.validation.maxLength || field.validation.regex) && (
                                                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                                                    {field.defaultValue !== undefined && (
                                                        <div className="bg-blue-50 text-blue-700 px-2 py-1 rounded">
                                                            Default: <span className="font-medium">{getFormattedValue(field.defaultValue, field.fieldType)}</span>
                                                        </div>
                                                    )}
                                                    {field.validation.minLength && (
                                                        <div className="bg-green-50 text-green-700 px-2 py-1 rounded">
                                                            Min Length: <span className="font-medium">{field.validation.minLength}</span>
                                                        </div>
                                                    )}
                                                    {field.validation.maxLength && (
                                                        <div className="bg-yellow-50 text-yellow-700 px-2 py-1 rounded">
                                                            Max Length: <span className="font-medium">{field.validation.maxLength}</span>
                                                        </div>
                                                    )}
                                                    {field.validation.regex && (
                                                        <div className="bg-purple-50 text-purple-700 px-2 py-1 rounded w-full mt-1">
                                                            Regex: <span className="font-mono">{field.validation.regex}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <div className="mt-4 border-2 border-dashed border-gray-300 rounded-md p-6 text-center">
                                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                                </svg>
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No fields added yet</h3>
                                <p className="mt-1 text-sm text-gray-500">Use the form below to add fields to this form.</p>
                            </div>
                        )}

                        {/* Add New Field Form */}
                        <div className="mt-6 bg-gray-50 p-4 rounded-md border border-gray-200">
                            <h5 className="font-medium text-gray-900 mb-4">Add New Field</h5>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                {/* Field Name */}
                                <div>
                                    <label htmlFor="fieldName" className="block text-sm font-medium text-gray-700 mb-1">
                                        Field Name
                                    </label>
                                    <input
                                        id="fieldName"
                                        type="text"
                                        name="fieldName"
                                        value={fieldState.fieldName}
                                        onChange={handleFieldChange}
                                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        placeholder="e.g., Email Address"
                                    />
                                </div>

                                {/* Field Type */}
                                <div>
                                    <label htmlFor="fieldType" className="block text-sm font-medium text-gray-700 mb-1">
                                        Field Type
                                    </label>
                                    <select
                                        id="fieldType"
                                        name="fieldType"
                                        value={fieldState.fieldType}
                                        onChange={handleFieldChange}
                                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    >
                                        {fieldTypes.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {formatFieldType(type.value)}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Required Checkbox */}
                                <div className="flex items-end">
                                    <label className="inline-flex items-center cursor-pointer text-sm text-gray-700">
                                        <input
                                            type="checkbox"
                                            name="required"
                                            checked={fieldState.required}
                                            onChange={handleFieldChange}
                                            className="rounded border-gray-300 text-blue-600 shadow-sm focus:ring-blue-500 h-4 w-4 mr-2"
                                        />
                                        Required Field
                                    </label>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                {/* Default Value */}
                                <div>
                                    <label htmlFor="defaultValue" className="block text-sm font-medium text-gray-700 mb-1">
                                        Default Value
                                    </label>
                                    {renderFieldInput()}
                                </div>

                                {/* Min Length */}
                                <div>
                                    <label htmlFor="minLength" className="block text-sm font-medium text-gray-700 mb-1">
                                        Min Length
                                    </label>
                                    <input
                                        id="minLength"
                                        type="number"
                                        name="validation.minLength"
                                        value={fieldState.validation.minLength}
                                        onChange={handleFieldChange}
                                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        placeholder="Optional"
                                    />
                                </div>

                                {/* Max Length */}
                                <div>
                                    <label htmlFor="maxLength" className="block text-sm font-medium text-gray-700 mb-1">
                                        Max Length
                                    </label>
                                    <input
                                        id="maxLength"
                                        type="number"
                                        name="validation.maxLength"
                                        value={fieldState.validation.maxLength}
                                        onChange={handleFieldChange}
                                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        placeholder="Optional"
                                    />
                                </div>
                            </div>

                            {/* Validation Regex */}
                            <div className="mb-4">
                                <label htmlFor="validationRegex" className="block text-sm font-medium text-gray-700 mb-1">
                                    Validation Regex
                                </label>
                                <input
                                    id="validationRegex"
                                    type="text"
                                    name="validation.regex"
                                    value={fieldState.validation.regex}
                                    onChange={handleFieldChange}
                                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    placeholder="e.g., ^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$"
                                />
                            </div>

                            <button
                                type="button"
                                onClick={handleAddField}
                                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                            >
                                <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                </svg>
                                Add Field
                            </button>
                        </div>
                    </div>
                    {error && (
                        <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 rounded">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-700">{error}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mt-8 border-t border-gray-200 pt-6 flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={() => setShowModal(false)}
                            className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            {isEditing ? 'Update Form' : 'Add Form'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default React.memo(FormModal);